// apps/admin/src/pages/auth/LoginPage.tsx
//
// vg-binding: CONTEXT:decisions (P999.11.D-09/D-15/D-16/D-08 — auth cluster to pw tokens)
// vg-binding: INTERFACE-STANDARDS:error-shape (N/A — pure FE restyle, no API surface)
//
// Phase 1 Wave 9 Task 45 — Admin LoginPage.
//
// Variant rules vs Merchant:
//   - NO register link (: admins are provisioned via Setup Wizard only)
//   - OAuth: Lark only (cross-role boundary)
//
// Goals:.
// SPECS. CONTEXT.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';

import { useAuth } from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { API_BASE_URL } from '../../lib/apiClient.js';
import { setSessionHint } from '../../lib/sessionHint.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// Phase 8.x debug B1 — pre-auth locale switcher. Anonymous visitors need
// to be able to flip the UI language on /login (et al.) before they have
// a session; the selector itself short-circuits the PATCH when `user` is
// null so we don't 401-spam the audit log.
import { AdminLanguageSelector } from '../../components/shell/AdminLanguageSelector.js';

// --- useRecaptcha (duplicated from merchant per Wave 9 task scope) ---------

interface GrecaptchaApi {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

function loadRecaptchaScript(siteKey: string): Promise<GrecaptchaApi> {
  if (!siteKey) {
    return Promise.resolve({
      ready: (cb) => {
        cb();
      },
      execute: async () => 'mock-recaptcha-token-bypass-dev',
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w.grecaptcha && typeof w.grecaptcha.execute === 'function') {
    return Promise.resolve(w.grecaptcha as GrecaptchaApi);
  }
  return new Promise<GrecaptchaApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-pw-recaptcha]',
    );
    if (existing) {
      existing.addEventListener('load', () => {
        resolve((window as unknown as { grecaptcha: GrecaptchaApi }).grecaptcha);
      });
      existing.addEventListener('error', () => reject(new Error('RECAPTCHA_LOAD_FAILED')));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.pwRecaptcha = '1';
    script.addEventListener('load', () => {
      const api = (window as unknown as { grecaptcha?: GrecaptchaApi }).grecaptcha;
      if (api) resolve(api);
      else reject(new Error('RECAPTCHA_NOT_AVAILABLE'));
    });
    script.addEventListener('error', () => reject(new Error('RECAPTCHA_LOAD_FAILED')));
    document.head.appendChild(script);
  });
}

export function useRecaptcha(siteKey: string): {
  getToken: (action: string) => Promise<string>;
} {
  return useMemo(
    () => ({
      async getToken(action: string): Promise<string> {
        const api = await loadRecaptchaScript(siteKey);
        return new Promise<string>((resolve, reject) => {
          api.ready(() => {
            api
              .execute(siteKey, { action })
              .then(resolve)
              .catch((err: unknown) =>
                reject(err instanceof Error ? err : new Error('RECAPTCHA_EXEC_FAILED')),
              );
          });
        });
      },
    }),
    [siteKey],
  );
}

// --- Form schema -----------------------------------------------------------

type Translate = (key: string, vars?: Record<string, unknown>) => string;

function makeLoginSchema(t: Translate) {
  return z.object({
    email: z.string().email(t('auth.login.validation.emailInvalid')).max(255),
    password: z
      .string()
      .min(8, t('auth.login.validation.passwordMin'))
      .max(128, t('auth.login.validation.passwordMax')),
  });
}
interface LoginInput {
  email: string;
  password: string;
}

// --- Helpers ---------------------------------------------------------------

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
}

function readErrorCode(payload: unknown, status: number): string {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as { success?: boolean }).success === false &&
    'error' in payload
  ) {
    const code = (payload as ErrorEnvelope).error?.code;
    if (typeof code === 'string') return code;
  }
  if (status === 401) return 'AUTH_INVALID_CREDENTIALS';
  if (status === 403) return 'AUTH_RECAPTCHA_FAILED';
  if (status === 423) return 'AUTH_ACCOUNT_LOCKED';
  if (status === 428) return 'AUTH_EMAIL_UNVERIFIED';
  if (status === 429) return 'RATE_LIMIT';
  return `HTTP_${status}`;
}

type Translator = (key: string, vars?: Record<string, unknown>) => string;

/**
 * Localized auth-error mapper (admin portal). Mirrors the shared
 * `describeAuthError` code map but resolves copy via the `ops` namespace
 * (`auth.login.errors.*`) so messages follow the active locale (EN/VI/FR)
 * instead of the hardcoded Vietnamese in `@printway/ui`.
 */
function describeAuthErrorI18n(
  code: string,
  t: Translator,
  ctx?: { lockedUntil?: number | null },
): string {
  const E = 'auth.login.errors.';
  switch (code) {
    case 'AUTH_INVALID_CREDENTIALS':
      return t(E + 'invalidCredentials');
    case 'AUTH_RECAPTCHA_FAILED':
      return t(E + 'recaptchaFailed');
    case 'AUTH_EMAIL_UNVERIFIED':
      return t(E + 'emailUnverified');
    case 'AUTH_2FA_REQUIRED':
      return t(E + 'twoFaRequired');
    case 'AUTH_ACCOUNT_LOCKED': {
      if (ctx?.lockedUntil) {
        const remainingMs = Math.max(0, ctx.lockedUntil - Date.now());
        const mm = String(Math.floor(remainingMs / 60000)).padStart(2, '0');
        const ss = String(Math.floor((remainingMs % 60000) / 1000)).padStart(
          2,
          '0',
        );
        return t(E + 'accountLockedCountdown', { time: `${mm}:${ss}` });
      }
      return t(E + 'accountLocked');
    }
    case 'AUTH_PENDING_APPROVAL':
      return t(E + 'pendingApproval');
    case 'AUTH_RATE_LIMIT_EXCEEDED':
    case 'RATE_LIMIT':
      return t(E + 'rateLimit');
    case 'VALIDATION_FAILED':
      return t(E + 'validationFailed');
    case 'AUTH_REQUIRED':
      return t(E + 'authRequired');
    case 'FORBIDDEN':
    case 'AUTH_PERMISSION_DENIED':
      return t(E + 'forbidden');
    default:
      return t(E + 'default');
  }
}

function useLockoutCountdown(unlockAt: number | null): string | null {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (unlockAt === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [unlockAt]);
  if (unlockAt === null) return null;
  const remaining = Math.max(0, Math.floor((unlockAt - now) / 1000));
  if (remaining <= 0) return null;
  const mm = Math.floor(remaining / 60).toString().padStart(2, '0');
  const ss = (remaining % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

// Lark Suite official brand logo (served from /public/lark-suite-logo.png).
function LarkIcon({ className }: { className?: string }): JSX.Element {
  return (
    <img
      src="/lark-suite-logo.png"
      alt="Lark Suite"
      className={className}
      aria-hidden="true"
    />
  );
}

// --- Page ------------------------------------------------------------------

export default function LoginPage(): JSX.Element {
  const { t } = useTranslation('ops');
  const navigate = useNavigate();
  const location = useLocation();
  const fetchMe = useAuth((s) => s.fetchMe);
  const isLoading = useAuth((s) => s.isLoading);

  // Post-login redirect target. AuthGuard boots unauthenticated users
  // to /login with `state.from = <pathname+search>`; SessionExpiredModal uses
  // a `?from=` query param. Prefer state, fall back to query, then `/`.
  // Reject absolute/protocol-relative URLs to avoid open-redirect (only
  // same-origin in-app paths starting with a single `/`).
  const returnTo = useMemo(() => {
    const fromState = (location.state as { from?: unknown } | null)?.from;
    const fromQuery = new URLSearchParams(location.search).get('from');
    const candidate =
      (typeof fromState === 'string' && fromState) || fromQuery || '/';
    return candidate.startsWith('/') && !candidate.startsWith('//')
      ? candidate
      : '/';
  }, [location.state, location.search]);

  // One-shot flash passed via router state (e.g. ResetPasswordPage on success
  // navigates to /login with { flash: { variant, title, desc } }). Strings are
  // already localized at the source, so render them verbatim.
  const flash = (location.state as {
    flash?: { variant?: string; title?: string; desc?: string };
  } | null)?.flash;

  const recaptchaSiteKey =
    (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined) ?? '';
  const { getToken } = useRecaptcha(recaptchaSiteKey);

  const LoginSchema = useMemo(() => makeLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema as never),
    defaultValues: { email: '', password: '' },
  });

  const [serverError, setServerError] = useState<string | null>(null);
  const [lockUntil, setLockUntil] = useState<number | null>(null);
  const [stepUp, setStepUp] = useState<{ challengeToken: string } | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const lockCountdown = useLockoutCountdown(lockUntil);

  const onSubmit = async (values: LoginInput): Promise<void> => {
    setServerError(null);
    setSubmitting(true);
    try {
      const recaptchaToken = await getToken('login');
      const client = apiClient();
      const { data, error, response } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).POST('/api/v1/auth/login', {
        body: {
          email: values.email,
          password: values.password,
          recaptchaToken,
        },
      });

      if (!response.ok) {
        // openapi-fetch puts the error body in `error` (not `data`) on non-2xx;
        // reading only `data` dropped the real code → readErrorCode fell back to
        // the status map (403 → AUTH_RECAPTCHA_FAILED). Pass the error body.
        const code = readErrorCode(data ?? error, response.status);
        let lockedUntil: number | null = null;
        if (code === 'AUTH_ACCOUNT_LOCKED' || response.status === 423) {
          const retryAfter = response.headers.get('retry-after');
          const waitSec = retryAfter ? parseInt(retryAfter, 10) : 15 * 60;
          lockedUntil = Date.now() + waitSec * 1000;
          setLockUntil(lockedUntil);
        }
        setServerError(describeAuthErrorI18n(code, t, { lockedUntil }));
        return;
      }

      const payload = data as
        | {
            success: true;
            data: {
              outcome:
                | 'authenticated'
                | 'requires_2fa'
                | 'step_up'
                | 'setup_2fa_required';
              method?: 'TOTP' | 'EMAIL';
              challengeToken?: string;
            };
          }
        | undefined;

      const outcome = payload?.data?.outcome;
      // P8.D-508 — mandatory first-login 2FA enrollment. The session is
      // restricted server-side to the 2FA-setup allowlist; land the user
      // on the Setup 2FA screen instead of the app shell.
      if (outcome === 'setup_2fa_required') {
        setSessionHint();
        await fetchMe();
        navigate('/settings/2fa', { replace: true });
        return;
      }
      if (outcome === 'requires_2fa') {
        const method = (payload?.data?.method ?? 'TOTP').toLowerCase();
        if (payload?.data?.challengeToken) {
          try {
            sessionStorage.setItem('pw_2fa_challenge', payload.data.challengeToken);
          } catch {
            // private mode — verify page surfaces "phiên hết hạn".
          }
        }
        // G-21 — carry the deep-link target through the 2FA hop so the
        // verify page can land the user back where they started.
        const fromQs =
          returnTo !== '/' ? `&from=${encodeURIComponent(returnTo)}` : '';
        navigate(
          `/verify-2fa?method=${method === 'email' ? 'email' : 'totp'}${fromQs}`,
        );
        return;
      }
      if (outcome === 'step_up' && payload?.data?.challengeToken) {
        setStepUp({ challengeToken: payload.data.challengeToken });
        return;
      }
      setSessionHint();
      await fetchMe();
      navigate(returnTo, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'LOGIN_FAILED';
      setServerError(describeAuthErrorI18n(message, t));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitOtp = async (): Promise<void> => {
    if (!stepUp) return;
    if (!/^\d{6}$/.test(otpCode)) {
      setOtpError(t('auth.login.stepUp.otpInvalid'));
      return;
    }
    setOtpError(null);
    try {
      const client = apiClient();
      const { response } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{ response: Response }>;
        }
      ).POST('/api/v1/auth/verify-2fa', {
        body: {
          challengeToken: stepUp.challengeToken,
          code: otpCode,
          rememberDevice: false,
        },
      });
      if (!response.ok) {
        setOtpError(t('auth.login.stepUp.otpWrong'));
        return;
      }
      setSessionHint();
      await fetchMe();
      navigate(returnTo, { replace: true });
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'OTP_VERIFY_FAILED');
    }
  };

  // UAT #2333 (parity with vendor LoginPage): this is a raw browser navigation
  // (<a href>), not an apiClient call, so it bypasses the axios baseURL. A bare
  // relative `/api/...` href resolves to the FE origin and 404s in split-origin
  // sandbox. Prefix API_BASE_URL so the browser hits the API host directly.
  const oauthLarkHref = API_BASE_URL + '/api/v1/auth/oauth/lark/authorize';

  const inputBase =
    'w-full rounded-xl bg-pw-surface px-4 py-3.5 text-base text-pw-text placeholder:text-pw-text-muted outline-none transition focus:ring-2 focus:ring-orange-400/60';

  return (
    <div className="min-h-screen bg-pw-bg" data-testid="auth-split-layout">
      <div className="absolute right-6 top-6">
        <AdminLanguageSelector />
      </div>

      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
        <div className="flex justify-center">
          <img
            src="/printway-logo.avif"
            alt="Printway"
            className="h-10 w-auto"
          />
        </div>

        <h1 className="mt-10 text-center text-4xl font-bold tracking-tight text-pw-text">
          {t('auth.login.title')}
        </h1>
        <p className="mt-3 text-center text-base text-pw-text-muted">
          {t('auth.login.subtitle')}
        </p>

        {flash?.title ? (
          <div
            role="status"
            data-testid="login-flash-banner"
            className="mt-6 rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 px-3 py-2 text-sm text-green-800 dark:text-green-300"
          >
            <span className="font-semibold">{flash.title}</span>
            {flash.desc ? <span className="block">{flash.desc}</span> : null}
          </div>
        ) : null}

        {lockCountdown ? (
          <div
            role="alert"
            data-testid="lockout-banner"
            className="mt-6 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-800 dark:text-red-300"
          >
            {t('auth.login.lockout.before')}{' '}
            <span data-testid="lockout-countdown" className="font-semibold">
              {lockCountdown}
            </span>
            .
          </div>
        ) : null}

        {!stepUp ? (
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="mt-10 space-y-5"
          >
            <div>
              <label
                htmlFor="login-email"
                className="mb-2 block text-sm font-medium text-pw-text"
              >
                {t('auth.login.emailLabel')}
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                placeholder={t('auth.login.emailPlaceholder')}
                aria-invalid={errors.email ? 'true' : 'false'}
                disabled={!!lockCountdown || submitting || isLoading}
                className={inputBase}
                {...register('email')}
              />
              {errors.email ? (
                <p className="mt-1 text-sm text-red-600 dark:text-red-300">{errors.email.message}</p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="mb-2 block text-sm font-medium text-pw-text"
              >
                {t('auth.login.passwordLabel')}
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t('auth.login.passwordPlaceholder')}
                  aria-invalid={errors.password ? 'true' : 'false'}
                  disabled={!!lockCountdown || submitting || isLoading}
                  className={`${inputBase} pr-12`}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                  className="absolute inset-y-0 right-3 flex items-center text-pw-text-muted hover:text-pw-text"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password ? (
                <p className="mt-1 text-sm text-red-600 dark:text-red-300">{errors.password.message}</p>
              ) : null}
            </div>

            <div className="flex justify-end">
              <a
                href="/forgot-password"
                className="text-sm font-medium text-orange-500 hover:text-orange-600"
              >
                {t('auth.login.forgotPassword')}
              </a>
            </div>

            {serverError && !lockCountdown ? (
              <p
                role="alert"
                data-testid="login-server-error"
                className="text-sm font-medium text-red-600 dark:text-red-300"
              >
                {serverError}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={!!lockCountdown || submitting || isLoading}
              data-testid="login-submit"
              className="w-full rounded-xl bg-orange-500 px-4 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting || isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
            </button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-pw-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-pw-bg px-3 text-pw-text-muted">{t('auth.login.or')}</span>
              </div>
            </div>

            <a
              href={oauthLarkHref}
              data-testid="oauth-lark-button"
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-pw-border bg-pw-bg px-4 py-3.5 text-base font-medium text-pw-text transition hover:bg-pw-surface"
            >
              <LarkIcon className="h-5 w-5" />
              {t('auth.login.continueLark')}
            </a>

            {/* NO register link on admin portal. */}
            <p
              className="pt-2 text-center text-xs text-pw-text-muted"
              data-testid="admin-no-register-notice"
            >
              {t('auth.login.noRegisterNotice')}
            </p>
          </form>
        ) : (
          <div className="mt-10 space-y-4" data-testid="step-up-otp-section">
            <p className="text-sm text-pw-text">
              {t('auth.login.stepUp.prompt')}
            </p>
            <div>
              <label
                htmlFor="login-stepup-otp"
                className="mb-2 block text-sm font-medium text-pw-text"
              >
                {t('auth.login.stepUp.otpLabel')}
              </label>
              <input
                id="login-stepup-otp"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={otpCode}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className={inputBase}
              />
              {otpError ? (
                <p className="mt-1 text-sm text-red-600 dark:text-red-300">{otpError}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => {
                void onSubmitOtp();
              }}
              data-testid="step-up-otp-submit"
              className="w-full rounded-xl bg-orange-500 px-4 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-orange-600"
            >
              {t('auth.login.stepUp.verify')}
            </button>
            <button
              type="button"
              onClick={() => {
                setStepUp(null);
                setOtpCode('');
                setOtpError(null);
              }}
              className="w-full rounded-xl px-4 py-3.5 text-base font-medium text-pw-text-muted hover:bg-pw-surface"
            >
              {t('auth.login.stepUp.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
